import React, { useContext } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChessBoard, faXmark } from '@fortawesome/free-solid-svg-icons';
import GameContext from '../context/GameContext';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import { useCorrections } from './CorrectionsProvider';
import { usePractice } from './PracticeProvider';

// The "Repair these moves" action, sitting next to Re-evaluate in the overlay header — a split button
// mirroring the evaluate control. The main half enters/exits repair mode in place on the board; the
// caret opens the severity picker (which categories to repair), each a checkbox with its count.
// Deselecting a category drops it from practice AND from the score. The dropdown stays open on a
// checkbox click (autoClose="outside") so you can toggle several without it snapping shut.
//
// Only meaningful once the game is evaluated: renders nothing before evaluation, and a quiet
// "no mistakes" note for a clean game.
export default function GamesOverlayRepair(): React.ReactNode {
  const { loading, hasMistakes, categories, toggleCategory, solvedCount, totalCount } =
    useCorrections();
  const practice = usePractice();
  const { game } = useContext(GameContext);

  // Nothing to repair → nothing to show (no "all clear" note). Also hidden before evaluation.
  if (game.LastEvaluation == null || loading || !hasMistakes) {
    return null;
  }

  return (
    <Dropdown as="div" className="cm-repair-split" autoClose="outside">
      <Button
        variant={practice.active ? 'outline-secondary' : 'primary'}
        className="cm-repair-btn"
        disabled={!practice.active && totalCount === 0}
        onClick={practice.active ? practice.stop : practice.start}
      >
        <FontAwesomeIcon icon={practice.active ? faXmark : faChessBoard} className="me-2" />
        {practice.active ? 'Exit repairing' : 'Fix Mistakes'}
        <span className="cm-repair-count">
          {solvedCount}/{totalCount}
        </span>
      </Button>
      <Dropdown.Toggle
        split
        variant={practice.active ? 'outline-secondary' : 'primary'}
        className="cm-repair-caret"
        aria-label="Choose which mistakes to repair"
      />
      <Dropdown.Menu align="end" className="cm-repair-menu">
        <Dropdown.Header>Which mistakes to repair</Dropdown.Header>
        {categories.map((c) => {
          const Icon = getIcon(c.moveType);
          return (
            <label key={c.key} className={`cm-repair-cat${c.enabled ? ' is-on' : ''}`}>
              <input type="checkbox" checked={c.enabled} onChange={() => toggleCategory(c.key)} />
              <span className={`cm-repair-cat-icon ${moveTypeClass(c.moveType)}`}>
                <Icon />
              </span>
              <span className="cm-repair-cat-label">{c.label}</span>
              <span className="cm-repair-cat-count">{c.count}</span>
            </label>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
